import { Header, Layout1, Sidebar, StackedTitle } from '@ljagis/react-core';
import {
  GeolocationButton,
  Identify,
  Layers,
  LocationSearch,
  Map,
  MapOverlay,
  StyleGallery,
  ZoomInButton,
  ZoomOutButton,
  useMapIdentify,
} from '@ljagis/react-mapping';
import { Box, Divider, IconButton, Tooltip } from '@material-ui/core';
import IdentifyIcon from '@material-ui/icons/FeaturedPlayListOutlined';
import LayersIcon from '@material-ui/icons/LayersOutlined';
import React, { useEffect, useState } from 'react';

import logo from '../assets/img/logo.png';
import { searchLocation } from '../services/searchLocation';

const {
  REACT_APP_DITCH_SEARCH_URL: ditchSearchUrl = '',
  REACT_APP_MAPBOX_ACCESS_TOKEN: mapboxAccessToken = '',
} = process.env;

export interface SharedPublicPageProps {
  title: string;
  subtitle: string;
  mapStyles: { title: string; previewImage: string; previewImage2x: string; styleUrl: string }[];
}

type sidebars = 'layers' | 'identify';

const SharedPublicPage: React.FC<SharedPublicPageProps> = ({ title, subtitle, mapStyles }) => {
  const {
    layerNames: identifyLayers,
    features: identifyFeatures,
    location: identifyLocation,
    clearFeatures: clearIdentify,
  } = useMapIdentify();

  const [sidebar, setSidebar] = useState<sidebars | undefined>('layers');

  const sidebars: Record<sidebars, { title: string; content: React.ReactNode }> = {
    layers: {
      title: 'Layers',
      content: (
        <Box flex={1} display="flex" flexDirection="column" minHeight={0}>
          <Layers flex={1} />
          <Divider />
          <StyleGallery items={mapStyles} />
        </Box>
      ),
    },
    identify: {
      title: 'Feature Details',
      content: (
        <Identify
          flex={1}
          layerNames={identifyLayers}
          features={identifyFeatures}
          location={identifyLocation}
          onClearFeatures={clearIdentify}
        />
      ),
    },
  };

  // Open identify sidebar on any new identified features
  useEffect(() => {
    if (!identifyFeatures.length) {
      return;
    }
    setSidebar('identify');
  }, [identifyFeatures]);

  return (
    <Layout1
      height={'100vh'}
      header={
        <Header minHeight={64} color="secondary">
          <img src={logo} alt="" style={{ height: 56, width: 56, marginRight: 16 }} />
          <Box flex="1 1 auto">
            <StackedTitle title={title} subtitle={subtitle} />
          </Box>
          <Tooltip title="Layers">
            <IconButton color="inherit" onClick={() => setSidebar('layers')}>
              <LayersIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Feature Details">
            <IconButton color="inherit" edge="end" onClick={() => setSidebar('identify')}>
              <IdentifyIcon />
            </IconButton>
          </Tooltip>
        </Header>
      }
      map={<Map style={{ height: '100%' }} initialMapStyle={mapStyles[0].styleUrl} />}
      mapOverlayTopLeft={[
        <MapOverlay key="locationSearch">
          <Box key={1} minWidth={240}>
            <LocationSearch
              placeholder="Search for an address or ditch"
              onSearch={searchLocation(mapboxAccessToken, ditchSearchUrl)}
            />
          </Box>
        </MapOverlay>,
      ]}
      mapOverlayTopRight={[
        <MapOverlay key="navigation">
          <Box>
            <ZoomInButton />
            <Divider />
            <ZoomOutButton />
          </Box>
        </MapOverlay>,
        <MapOverlay key="geolocation">
          <GeolocationButton />
        </MapOverlay>,
      ]}
      sidebar={
        sidebar && (
          <Sidebar
            height={1}
            title={sidebars[sidebar].title}
            onClose={() => setSidebar(undefined)}
            content={sidebars[sidebar].content}
          />
        )
      }
    />
  );
};

export default SharedPublicPage;
